import {
  contributions,
  IGwApiService,
  IModuleLoader,
  inject,
  injectable,
  IPageParseService,
  IPageSetupResource,
  PreloadModuleInfo,
  PreloadDataInfo,
} from '@meego-remote/core/app/contract';
import { getHeavyComConfigInfo } from './HeavyCompsPrefetch/utils';
import { PrefetchParams } from './HeavyCompsPrefetch/type';
import { DetailPageKey, DetailPageParams, IComponentPreloadContribution } from '../contract';

enum DetailType {
  State = 'state',
  Workflow = 'workflow',
  Default = 'default',
}

@injectable()
export class DetailPageSetupResource extends IPageSetupResource {
  pageKey = DetailPageKey;

  @inject(IPageParseService)
  private pageParseService: IPageParseService;
  @inject(IGwApiService)
  private gwApiService: IGwApiService;
  @inject(IModuleLoader)
  private moduleLoader: IModuleLoader;
  @contributions(IComponentPreloadContribution)
  private componentPreloadContributions: IComponentPreloadContribution[];

  getPreloadModuleFast() {
    return [];
  }

  async getPreloadModule(): Promise<PreloadModuleInfo[]> {
    const list: PreloadModuleInfo[] = [];
    const widgetFileList = [
      'core',
      'form-manifest',
      'preset-form-display',
      // `others-form-display`,
      // `preset-form-edit`,
      // `others-form-edit`,
    ];
    widgetFileList.forEach((name) => {
      list.push({
        type: 'module',
        key: '@meego-remote/widget/ui-components',
        m: `./${name}`,
        opt: { async: true },
      });
    });

    // 异步获取重组件预取配置
    const heavyPromise = this.getHeavyCompsConfigInfo();

    const detailType = await this.getDetailType();
    this.logger.log('DetailPageSetupResource getPreloadModule', detailType);

    if (!detailType) {
      return list;
    }

    switch (detailType) {
      case DetailType.State:
        list.unshift({
          type: 'module',
          key: '@meego-remote/main',
          m: 'workItem-detail-stateDetail',
          opt: { async: true },
        });
        break;

      case DetailType.Workflow:
        list.unshift({
          type: 'module',
          key: '@meego-remote/main',
          m: 'workItem-detail-workflowDetail',
          opt: { async: true },
        });
        break;

      default:
        list.unshift({
          type: 'module',
          key: '@meego-remote/main',
          m: 'workItem-detail-completeDetail',
          opt: { async: true },
        });
    }

    list.map((item) => {
      this.moduleLoader.load(item);
    });

    await heavyPromise;
    this.logger.log('DetailPageSetupResource getPreloadModule heavyPromise done');
    return [];
  }

  private async getDetailType() {
    const { currentPage } = this.pageParseService;
    if (!currentPage) {
      return;
    }
    const { simpleName, workItemType, workItemID } = (currentPage.params || {}) as DetailPageParams;

    const { searchParams } = new URL(`https://xx/xxx${currentPage.location.search || ''}`);
    const params = {
      project_simple_name: simpleName,
      work_item_api_name: workItemType,
      work_item_id: Number(workItemID),
    } as Record<string, string | number>;
    if (searchParams.get('activeStateId')) {
      params.node_key = searchParams.get('activeStateId')!;
    }
    this.logger.log('getDetailType params', params);
    const info = await this.gwApiService.request('APIFetchMinimalInfoWorkItem', params);

    const { workflow_mode, need_node_details } = info?.data?.data?.value || {};
    // 非节点流，一定是状态流
    if (workflow_mode === false) return DetailType.State;
    // 节点流，且不需要节点详情
    if (workflow_mode && need_node_details === false) return DetailType.Workflow;
    // 节点流，且需要节点详情，或者case其他兜底
    return DetailType.Default;
  }

  private async getHeavyCompsConfigInfo() {
    const { currentPage } = this.pageParseService;
    if (!currentPage) {
      return;
    }
    // 这个workItemType是api name
    const { simpleName, workItemType, workItemID } = (currentPage.params || {}) as DetailPageParams;
    const { searchParams } = new URL(`https://xx/xxx${currentPage.location.search || ''}`);
    const defaultTabKey = searchParams.get('tabKey') || searchParams.get('tab_key') || undefined;
    const defaultNodeKey = searchParams.get('activeStateId') || undefined;

    const moduleParams = {
      first_screen: true,
      tab_key: defaultTabKey,
      node_key: defaultNodeKey,
      visible_area: {
        enable: true,
      },
    };

    const params = {
      project_simple_name: simpleName,
      work_item_api_name: workItemType,
      work_item_id: Number(workItemID),
      modules: [
        {
          key: 'detail',
          params: moduleParams,
        },
      ],
      version: '2',
    };
    this.logger.log('getHeavyCompsConfigInfo params', workItemID, params);

    const info = await this.gwApiService.request('APIDemandFetchWorkItem', params);

    const heavyCompsConfigInfo =
      getHeavyComConfigInfo({
        defaultNodeKey,
        defaultTabKey,
        data: info?.data?.data,
      }) || {};
    const extraInfo = info?.data?.data?.extra_info;
    const projectKey = extraInfo?.project_key;
    const workItemTypeKey = extraInfo?.work_item_type_key;

    this.logger.log(
      'heavyCompsConfigInfo',
      Object.keys(heavyCompsConfigInfo).join(','),
      projectKey,
      workItemTypeKey,
      this.componentPreloadContributions.length,
    );
    const preloadPromiseList: Promise<PreloadDataInfo|PreloadModuleInfo[]>[] = [];

    if (projectKey && workItemTypeKey) {
        Object.keys(heavyCompsConfigInfo).map(async (s) => {
          const confInfos = heavyCompsConfigInfo[s];
          const handler = this.componentPreloadContributions.find((contribution) => contribution.key === s);
          this.logger.log(`HeavyCompsPrefetchConfig ${s}`, confInfos.length, 'has handler:', !!handler);
          if (!handler) {
            return;
          }
            confInfos.map(async (confInfo: any) => {
              const preloadParams = {
                ...confInfo,
                project_key: projectKey,
                work_item_type_key: workItemTypeKey,
                work_item_id: workItemID,
                project_simple_name: simpleName,
              } as PrefetchParams;

              // 数据预取
              preloadPromiseList.push(handler.preload(preloadParams).catch(err=>this.logger.error(`preload fail: ${confInfo}`, err)));

              // 资源预取
              if(handler?.preloadResources){
                preloadPromiseList.push(handler.preloadResources(preloadParams)
                .then(resources => {
                  if (Array.isArray(resources)) {
                    resources.forEach(resource => this.moduleLoader.load(resource));
                  }
                })
                .catch(err => this.logger.error(`preloadResources fail: ${confInfo.id}`, err)))
              }
            })
          this.logger.log(`HeavyCompsPrefetchConfig ${s} done`);
        })
    }
    return Promise.allSettled(preloadPromiseList);
  }
}
